import type { OrderAnalytic } from '@talos/kyoko';
import {
  BlotterDensity,
  BlotterTable,
  Box,
  Button,
  Card,
  formatDuration,
  formattedDate,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  IndicatorBadge,
  IndicatorBadgeVariants,
  MixpanelEvent,
  MixpanelEventSource,
  MixpanelSourceContext,
  type Order,
  parseDuration,
  Popover,
  timeBetween,
  toBigWithDefault,
  Tooltip,
  useBlotterTable,
  useMixpanel,
  usePopoverState,
} from '@talos/kyoko';
import type { BigSource } from 'big.js';
import { useMemo } from 'react';
import { of } from 'rxjs';
import { useFeatureFlag } from '../../hooks';
import { Link } from '../OMS/styles';
import { ANALYTIC_PROPERTIES } from './constants';
import { NotifyTooltip } from './NotifyTooltip';
import type { AnalyticName, TradePredictionRow } from './types';
import { useTradePredictionColumns } from './useTradePredictionColumns';

interface TradePredictionsProps {
  order: Order;
  latestAnalytics: Record<number, OrderAnalytic>;
}

export function TradePredictions({ order, latestAnalytics }: TradePredictionsProps) {
  const { enableTradePredictions } = useFeatureFlag();
  const mixpanel = useMixpanel();
  const columns = useTradePredictionColumns();

  const preTradePopOver = usePopoverState({
    trigger: 'hover',
    usePortal: true,
    noPaddingAndBorder: true,
    placement: 'bottom-start',
    onOpen: () => {
      mixpanel.track(MixpanelEvent.PreTrade);
    },
  });
  const simPopOver = usePopoverState({
    trigger: 'hover',
    usePortal: true,
    noPaddingAndBorder: true,
    placement: 'bottom-start',
    onOpen: () => {
      mixpanel.track(MixpanelEvent.SimulateTradeScenarios);
    },
  });

  // Get the latest analytics data
  const latestAnalytic = latestAnalytics[1];

  const dataObs = useMemo(() => {
    const startTime = order.StartTime || order.SubmitTime;
    const endTime = order.EndTime;

    const duration = endTime
      ? parseDuration(timeBetween(new Date(startTime), new Date(endTime), ['days', 'hours', 'minutes', 'seconds']))
      : null;
    const durationMs = duration
      ? duration.days * 86400 + duration.hours * 3600 + duration.minutes * 60 + duration.seconds
      : null;

    // Generate known analytic rows
    const knownRows: TradePredictionRow[] = [];

    // Always include Start Time and End Time
    knownRows.push({
      name: 'Start Time',
      interval: formattedDate(startTime),
      daily: null,
      vs: null,
    });

    knownRows.push({
      name: 'End Time',
      interval: formattedDate(endTime),
      daily: null,
      vs: null,
    });

    // Add known analytic properties only if they have data
    if (latestAnalytic?.hasTradePredictions) {
      for (const config of Object.values(ANALYTIC_PROPERTIES)) {
        const row = getTradePredictionRow(
          config.name,
          latestAnalytic.tradePredictionsMap,
          config.intervalKey,
          config.dailyKey,
          config.isPercentage
        );

        if (row) {
          knownRows.push({
            name: row.name,
            interval: row.interval,
            daily: row.daily,
            vs: row.vs,
          });
        }
      }
    }

    // Add Duration row
    knownRows.push({
      name: 'Duration',
      interval: formatDuration(duration),
      daily: '24h',
      vs: durationMs ? Math.min(1, durationMs / 86400) * 100 : null,
    });

    return of({
      initial: true,
      data: knownRows,
    });
  }, [latestAnalytic, order]);

  const blotterTable = useBlotterTable({
    dataObservable: dataObs,
    errorObservable: undefined,
    density: BlotterDensity.Compact,
    columns,
    rowID: 'name',
    gridOptions: {
      domLayout: 'autoHeight',
      autoSizeStrategy: {
        type: 'fitGridWidth',
      },
    },
  });

  if (!enableTradePredictions || !latestAnalytic?.hasTradePredictions) {
    return null;
  }

  return (
    <MixpanelSourceContext.Provider value={MixpanelEventSource.AnalyticsDrawer}>
      <Card
        title={
          <HStack gap="spacing8" alignItems="initial">
            <Tooltip tooltip="Trade Predictions highlight expected execution conditions for your order vs. daily market trends. Quickly gauge key metrics like spread, volume, volatility, and participation to fine-tune parameters and anticipate slippage.">
              Trade Predictions
            </Tooltip>
            <Link
              href="https://kb.talostrading.com/principal-trading/routing-and-algos/algo-and-safety-overview/#trade-predictions-supported-for-vwap-only"
              target="_blank"
            >
              <Icon icon={IconName.BookOpen} /> KB
            </Link>
            <IndicatorBadge children={<>NEW</>} variant={IndicatorBadgeVariants.Notification} />
          </HStack>
        }
        actions={
          <>
            <Popover {...preTradePopOver}>
              <Button endIcon={IconName.QuestionMarkCircle} size={FormControlSizes.Small}>
                Pre-Trade
              </Button>
              <NotifyTooltip
                title="Pre-Trade"
                tooltip="Pre-Trade provides actionable insights into potential order scenarios, helping users better understand how different strategies will perform and empowering them to make more informed decisions before executing trades."
                close={preTradePopOver.close}
                notifyMixpanelEvent={MixpanelEvent.PreTradeNotify}
              />
            </Popover>
            <Popover {...simPopOver}>
              <Button endIcon={IconName.QuestionMarkCircle} size={FormControlSizes.Small}>
                Simulate Trade Scenarios
              </Button>
              <NotifyTooltip
                title="Simulate Trade Scenarios"
                tooltip="Simulate your trade over the past real-data to get a perspective upon simulated slippage and impact. The tool attempts to simulate the trade with the same set of parameters and time horizon over past days, compute expected execution price, and compare to the several benchmarks. It then provides the distribution of possible expectation of slippage."
                close={simPopOver.close}
                notifyMixpanelEvent={MixpanelEvent.SimulateTradeScenariosNotify}
              />
            </Popover>
          </>
        }
        background="#338EEB1F"
      >
        <Box w="100%">
          <BlotterTable background="#338EEB00" {...blotterTable} />
        </Box>
      </Card>
    </MixpanelSourceContext.Provider>
  );
}

function getTradePredictionRow(
  name: AnalyticName,
  tradePredictionsMap: ReadonlyMap<string, string>,
  intervalKey: string,
  dailyKey?: string,
  isPercentage?: boolean
): TradePredictionRow | null {
  const intervalValue = tradePredictionsMap.get(intervalKey);
  const dailyValue = dailyKey ? tradePredictionsMap.get(dailyKey) : null;

  // Only show row if at least one value is present
  if (!intervalValue && !dailyValue) {
    return null;
  }

  const interval = intervalValue ? toBigWithDefault(intervalValue, 0) : null;
  const daily = dailyValue ? toBigWithDefault(dailyValue, 0) : null;

  // Calculate VS only if both values are present and daily is not zero
  let vs: BigSource | null = null;
  if (interval && daily && !daily.eq(0)) {
    vs = interval.div(daily).mul(100);
  }

  return {
    name,
    interval: interval ? (isPercentage ? interval.times(100) : interval) : null,
    daily: daily ? (isPercentage ? daily.times(100) : daily) : null,
    vs: vs,
  };
}
